import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { t, fmtDate } from '../i18n/index.jsx';

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications');
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = items.filter((n) => !Number(n.is_read)).length;

  async function openItem(n) {
    if (!Number(n.is_read)) {
      try {
        await api.patch(`/notifications/${n.id}/read`);
        setItems((list) => list.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  async function markAll() {
    try {
      await api.patch('/notifications/read-all');
      setItems((list) => list.map((x) => ({ ...x, is_read: 1 })));
    } catch { /* ignore */ }
  }

  return (
    <div className="notif-bell" ref={ref}>
      <button
        type="button"
        className={`notif-btn ${open ? 'open' : ''}`}
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        aria-label={t('Notifications')}
        aria-expanded={open}
      >
        <BellIcon />
        {unread > 0 && <span className="notif-dot">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-dropdown" role="menu">
          <div className="notif-head">
            <strong>{t('Notifications')}</strong>
            {unread > 0 && (
              <button type="button" className="btn ghost xs" onClick={markAll}>{t('Mark all read')}</button>
            )}
          </div>
          <div className="notif-list">
            {loading && !items.length ? (
              <div className="notif-empty muted">{t('Loading…')}</div>
            ) : items.length === 0 ? (
              <div className="notif-empty muted">{t('No notifications')}</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`notif-item ${Number(n.is_read) ? '' : 'unread'}`}
                  onClick={() => openItem(n)}
                >
                  <div className="notif-title">{n.title}</div>
                  {n.body && <div className="notif-body muted sm">{n.body}</div>}
                  <div className="notif-time muted sm">{fmtDate(String(n.created_at || '').slice(0, 10))}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
